import { useState } from 'react';

export default function PromotionForm() {
  const [formData, setFormData] = useState({
    eventName: '',
    organizerName: '',
    email: '',
    phone: '',
    eventDate: '',
    eventTime: '',
    venue: '',
    category: '',
    description: '',
    promotionType: 'featured',
    ticketing: 'no',
    expectedAttendance: '',
    ticketPrice: '',
    website: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({
        eventName: '',
        organizerName: '',
        email: '',
        phone: '',
        eventDate: '',
        eventTime: '',
        venue: '',
        category: '',
        description: '',
        promotionType: 'featured',
        ticketing: 'no',
        expectedAttendance: '',
        ticketPrice: '',
        website: '',
        additionalInfo: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const promotionPrices = {
    basic: '$25',
    featured: '$50',
    premium: '$75'
  };

  return (
    <section id="submit-form" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Submit Your Event for Promotion
          </h2>
          <p className="text-xl text-gray-600">
            Fill out the form below and we'll get your event promoted within 24 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
          {/* Event Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-calendar-event-line text-emerald-600"></i>
              Event Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="Summer Music Festival"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                >
                  <option value="">Select category</option>
                  <option value="music">Music & Concerts</option>
                  <option value="arts">Arts & Culture</option>
                  <option value="food">Food & Drink</option>
                  <option value="sports">Sports & Fitness</option>
                  <option value="family">Family & Kids</option>
                  <option value="business">Business & Networking</option>
                  <option value="community">Community</option>
                  <option value="education">Education & Workshops</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Date *
                </label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Time *
                </label>
                <input
                  type="time"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Venue / Location *
                </label>
                <input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="Maidu Community Center, 1550 Maidu Dr, Roseville, CA"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm resize-none"
                  placeholder="Describe your event, what attendees can expect, and why they should attend..."
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.description.length}/500 characters
                </div>
              </div>
            </div>
          </div>

          {/* Organizer Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-user-line text-emerald-600"></i>
              Organizer Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Organizer Name *
                </label>
                <input
                  type="text"
                  name="organizerName"
                  value={formData.organizerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="(916) 555-0123"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                  placeholder="https://yourevent.com"
                />
              </div>
            </div>
          </div>

          {/* Promotion Options */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-megaphone-line text-emerald-600"></i>
              Promotion Options
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {[
                { value: 'basic', label: 'Basic', price: promotionPrices.basic, desc: 'Standard listing' },
                { value: 'featured', label: 'Featured', price: promotionPrices.featured, desc: 'Homepage + badge' },
                { value: 'premium', label: 'Premium', price: promotionPrices.premium, desc: 'Maximum visibility' }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`relative flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.promotionType === option.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="promotionType"
                    value={option.value}
                    checked={formData.promotionType === option.value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">{option.label}</span>
                    <span className="text-emerald-600 font-bold">{option.price}</span>
                  </div>
                  <span className="text-xs text-gray-600">{option.desc}</span>
                  {formData.promotionType === option.value && (
                    <i className="ri-check-circle-fill text-emerald-600 absolute top-2 right-2"></i>
                  )}
                </label>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ticketing Required?
                </label>
                <select
                  name="ticketing"
                  value={formData.ticketing}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                >
                  <option value="no">No - Free Event</option>
                  <option value="yes">Yes - Paid Tickets (5-10% commission)</option>
                </select>
              </div>

              {formData.ticketing === 'yes' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ticket Price
                  </label>
                  <input
                    type="text"
                    name="ticketPrice"
                    value={formData.ticketPrice}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                    placeholder="$25"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Expected Attendance
                </label>
                <select
                  name="expectedAttendance"
                  value={formData.expectedAttendance}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm"
                >
                  <option value="">Select range</option>
                  <option value="0-50">0-50 people</option>
                  <option value="50-100">50-100 people</option>
                  <option value="100-250">100-250 people</option>
                  <option value="250-500">250-500 people</option>
                  <option value="500+">500+ people</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Additional Information
            </label>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-emerald-500 focus:outline-none text-sm resize-none"
              placeholder="Any special requirements, sponsorship opportunities, or additional details..."
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.additionalInfo.length}/500 characters
            </div>
          </div>

          {/* Submit Status */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg flex items-start gap-3">
              <i className="ri-check-circle-fill text-emerald-600 text-xl flex-shrink-0"></i>
              <div>
                <div className="font-semibold text-emerald-900 mb-1">Submission Received!</div>
                <div className="text-sm text-emerald-700">
                  We'll review your event and contact you within 24 hours with next steps and payment information.
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0"></i>
              <div>
                <div className="font-semibold text-red-900 mb-1">Submission Failed</div>
                <div className="text-sm text-red-700">
                  There was an error submitting your form. Please try again or contact support.
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 text-white py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Submit Event for Promotion</span>
                <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            By submitting this form, you agree to our terms of service and promotional guidelines.
          </p>
        </form>
      </div>
    </section>
  );
}